import React from "react";
import { Plus, Trash2, Calendar, MapPin, DollarSign, Siren } from "lucide-react";
import { CANONICAL_APPOINTMENT_TYPE_KEYS } from "./renovationSettingsDefaults";

export const APPOINTMENT_TYPE_LABELS = {
  free_quote_inspection: "Free Inspection",
  phone_video_consult: "Phone / Video Consult",
};

export const EMERGENCY_SAFETY_BRANCHES = [
  { key: "active_water_flooding", label: "Active water / flooding" },
  { key: "structural_collapse_or_imminent", label: "Structural collapse or imminent" },
  { key: "ceiling_collapse_or_imminent", label: "Ceiling collapse or imminent" },
  { key: "storm_electrical_involvement", label: "Storm + electrical involvement" },
  { key: "gas_or_chemical_smell_after_damage", label: "Gas or chemical smell" },
  { key: "default", label: "Default safety guidance" },
];

const inputClass =
  "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body";

export function PublicHolidaysEditor({ holidays = [], onChange }) {
  const list = Array.isArray(holidays) ? holidays : [];
  const updateRow = (idx, field, value) => {
    onChange(list.map((h, i) => (i === idx ? { ...h, [field]: value } : h)));
  };
  return (
    <div className="space-y-3">
      {list.map((h, idx) => (
        <div key={idx} className="p-3 rounded-lg bg-white/5 border border-white/10 grid grid-cols-1 md:grid-cols-3 gap-2">
          <input
            type="date"
            value={h.date || ""}
            onChange={(e) => updateRow(idx, "date", e.target.value)}
            className={inputClass}
            placeholder="YYYY-MM-DD"
          />
          <input
            type="text"
            value={h.name || ""}
            onChange={(e) => updateRow(idx, "name", e.target.value)}
            className={inputClass}
            placeholder="Holiday name"
          />
          <div className="flex gap-2">
            <input
              type="text"
              value={h.surcharge_fee || ""}
              onChange={(e) => updateRow(idx, "surcharge_fee", e.target.value)}
              className={inputClass}
              placeholder="Surcharge e.g. $100"
            />
            <button
              type="button"
              onClick={() => onChange(list.filter((_, i) => i !== idx))}
              className="text-[#9CA3AF] hover:text-red-400 p-1"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...list, { date: "", name: "", surcharge_fee: "$100" }])}
        className="flex items-center gap-2 text-[#00D1FF] text-sm"
      >
        <Plus className="w-4 h-4" /> Add holiday
      </button>
    </div>
  );
}

export function TeamCalendarsEditor({ calendars = [], onChange, appointmentTypeKeys = CANONICAL_APPOINTMENT_TYPE_KEYS }) {
  const list = Array.isArray(calendars) ? calendars : [];
  const updateRow = (idx, field, value) => {
    onChange(list.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };
  const toggleAppt = (idx, key) => {
    const row = list[idx] || {};
    const cur = row.handles_appointment_types || [];
    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    updateRow(idx, "handles_appointment_types", next);
  };
  return (
    <div className="space-y-4">
      {list.map((cal, idx) => (
        <div key={idx} className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-3">
          <div className="flex justify-between items-center">
            <span className="font-mono text-[#00D1FF] text-xs">Calendar {idx + 1}</span>
            <button type="button" onClick={() => onChange(list.filter((_, i) => i !== idx))} className="text-[#9CA3AF] hover:text-red-400">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <input className={inputClass} placeholder="Label (e.g. Consultant)" value={cal.label || ""} onChange={(e) => updateRow(idx, "label", e.target.value)} />
          <input className={inputClass} placeholder="Calendar ID (GHL)" value={cal.calendar_id || ""} onChange={(e) => updateRow(idx, "calendar_id", e.target.value)} />
          <input className={inputClass} placeholder="Platform (ghl)" value={cal.platform || ""} onChange={(e) => updateRow(idx, "platform", e.target.value)} />
          <p className="text-xs text-[#9CA3AF]">Handles appointment types:</p>
          <div className="flex flex-wrap gap-2">
            {appointmentTypeKeys.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => toggleAppt(idx, key)}
                className={`px-2 py-1 rounded text-xs border ${
                  (cal.handles_appointment_types || []).includes(key)
                    ? "bg-[#00D1FF]/20 border-[#00D1FF]/50 text-[#00D1FF]"
                    : "bg-white/5 border-white/10 text-[#9CA3AF]"
                }`}
              >
                {APPOINTMENT_TYPE_LABELS[key] || key}
              </button>
            ))}
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={() =>
          onChange([
            ...list,
            { platform: "ghl", calendar_id: "", label: "", handles_appointment_types: [] },
          ])
        }
        className="flex items-center gap-2 text-[#00D1FF] text-sm"
      >
        <Plus className="w-4 h-4" /> Add team calendar
      </button>
    </div>
  );
}

export function EmergencySafetyActionsEditor({ actions = {}, onChange }) {
  const update = (key, value) => onChange({ ...actions, [key]: value });
  return (
    <div className="space-y-3">
      {EMERGENCY_SAFETY_BRANCHES.map(({ key, label }) => (
        <div key={key}>
          <label className="text-xs text-[#9CA3AF] flex items-center gap-1 mb-1">
            <Siren className="w-3 h-3 text-[#00D1FF]" /> {label}
          </label>
          <textarea
            rows={2}
            className={inputClass}
            value={actions[key] || ""}
            onChange={(e) => update(key, e.target.value)}
          />
        </div>
      ))}
    </div>
  );
}

export function BookingGateKeysDisplay({ keys = [] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {(keys || []).map((k) => (
        <span key={k} className="px-2 py-1 rounded bg-[#00D1FF]/15 text-[#00D1FF] text-xs font-mono">
          {k}
        </span>
      ))}
    </div>
  );
}
